import { useEffect, useState, type ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Users,
  MapPin,
  Plane,
  Hotel,
  Car,
  Ticket,
  Plus,
  Shield,
  DollarSign,
  ReceiptText,
  Send,
  ExternalLink,
  FileText,
  CheckCircle2,
  Loader2,
  CircleDollarSign,
  Landmark,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BrazilFlag, USAFlag } from "@/components/ui/country-flags";
import { computeProposalFinancialSummary, formatMoney, MAX_DISCOUNT_PERCENTAGE } from "@/lib/financialCalculations";
import { formatPercentageForDisplay, maskPercentageInput, parsePercentageInput } from "@/lib/numberInput";
import { cn } from "@/lib/utils";

type PricingView = "adjusted" | "base";
type AdjustmentType = "none" | "commission" | "discount";

// Operator-facing summary uses a fixed locale so number formatting stays stable
// when the operator toggles the client-facing language (PT/EN). The language
// toggle only affects the proposal output (preview/PDF), not this internal view.
const SUMMARY_DISPLAY_LOCALE = "en-US";

interface ToggleChipProps {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  activeTone: "blue" | "emerald";
  icon: ReactNode;
  label: string;
}

const TONE_CLASSES: Record<ToggleChipProps["activeTone"], string> = {
  blue: "border-blue-600 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-500/15 dark:text-blue-200",
  emerald: "border-emerald-600 bg-emerald-50 text-emerald-700 dark:border-emerald-400 dark:bg-emerald-500/15 dark:text-emerald-200",
};

const ToggleChip = ({ active, onClick, disabled, activeTone, icon, label }: ToggleChipProps) => (
  <Button
    type="button"
    variant="outline"
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center gap-1.5 h-9 px-3 text-sm font-medium border-2 transition-all duration-150 rounded-lg ${
      active ? TONE_CLASSES[activeTone] : "border-border bg-background text-foreground hover:bg-muted/60"
    }`}
  >
    {icon}
    {label}
  </Button>
);

interface SummaryStepProps {
  data: any;
  onChange: (updates: any) => void;
  readOnly?: boolean;
  isNeedsFixSales?: boolean;
  loading?: boolean;
  previewingProposal?: boolean;
  generatingPdf?: boolean;
  onPreview?: (pricingView: PricingView) => void | Promise<void>;
  onGeneratePdf?: (pricingView: PricingView) => void | Promise<void>;
  onFinish?: () => void | Promise<void>;
  onApplyAdjustment?: (nextData: any) => Promise<void> | void;
}

const SummaryStep = ({
  data,
  onChange,
  readOnly = false,
  isNeedsFixSales,
  loading,
  previewingProposal,
  generatingPdf,
  onPreview,
  onGeneratePdf,
  onFinish,
  onApplyAdjustment,
}: SummaryStepProps) => {
  const {
    title,
    main_destination,
    passengerCount = { adults: 0, children: 0, infants: 0 },
    passenger_mode_detailed,
    passengers = [],
    flights = [],
    accommodations = [],
    transfers = [],
    tours = [],
    attractions = [],
    airportServices = [],
    additionalRequests = [],
    insurance = [],
    handlingFee = [],
  } = data;

  const pax_adults = passengerCount.adults || 0;
  const pax_children = passengerCount.children || 0;
  const pax_infants = passengerCount.infants || 0;
  const totalPax = pax_adults + pax_children + pax_infants;

  const proposalCurrency = (data?.currency || "").toString().trim().toUpperCase() === "AED" ? "AED" : "USD";
  const formatSummaryMoney = (value: number | null | undefined) => formatMoney(value, proposalCurrency, SUMMARY_DISPLAY_LOCALE);

  const adjustmentType = ((data?.pricing_adjustment_type || "none") as AdjustmentType);
  // Clamped the same way computeProposalFinancialSummary clamps it, so the strike-through
  // figures below cannot disagree with the totals on a legacy over-100% discount.
  const adjustmentPercentageRaw = Number(data?.pricing_adjustment_percentage || 0);
  const adjustmentPercentage =
    adjustmentType === "discount"
      ? Math.min(adjustmentPercentageRaw, MAX_DISCOUNT_PERCENTAGE)
      : adjustmentPercentageRaw;

  // Draft adjustment state — committed via the Apply button.
  const [draftAdjustmentType, setDraftAdjustmentType] = useState<string>(adjustmentType);
  const [draftPercentageInput, setDraftPercentageInput] = useState<string>(
    formatPercentageForDisplay(adjustmentPercentage)
  );
  const [isSavingAdjustment, setIsSavingAdjustment] = useState(false);
  const [adjustmentSavedAt, setAdjustmentSavedAt] = useState<number | null>(null);

  // Price-tier override: "auto" = derive from the client's partner (default).
  const currentPriceTier = ((data?.price_tier_override ?? "auto") as string) || "auto";
  const [draftPriceTier, setDraftPriceTier] = useState<string>(currentPriceTier);
  const [isSavingPriceTier, setIsSavingPriceTier] = useState(false);
  const [priceTierSavedAt, setPriceTierSavedAt] = useState<number | null>(null);
  const hasPendingPriceTierChange = draftPriceTier !== currentPriceTier;

  // VAT exemption: proposals for destinations outside the UAE don't charge VAT at all
  // (neither the fixed 5% nor the per-item variable tax percentages).
  const currentVatExempt = Boolean(data?.vat_exempt);
  const [draftVatExempt, setDraftVatExempt] = useState<boolean>(currentVatExempt);
  const [isSavingVatExempt, setIsSavingVatExempt] = useState(false);
  const [vatExemptSavedAt, setVatExemptSavedAt] = useState<number | null>(null);
  const hasPendingVatExemptChange = draftVatExempt !== currentVatExempt;

  useEffect(() => {
    setDraftAdjustmentType(adjustmentType);
    setDraftPercentageInput(formatPercentageForDisplay(adjustmentPercentage));
  }, [adjustmentType, adjustmentPercentage]);

  useEffect(() => {
    setDraftPriceTier(currentPriceTier);
  }, [currentPriceTier]);

  useEffect(() => {
    setDraftVatExempt(currentVatExempt);
  }, [currentVatExempt]);

  const applyPriceTier = async () => {
    const nextData = {
      ...data,
      price_tier_override: draftPriceTier === "auto" ? null : draftPriceTier,
    };
    if (!onApplyAdjustment) {
      onChange(nextData);
      return;
    }
    setIsSavingPriceTier(true);
    try {
      await onApplyAdjustment(nextData);
      setPriceTierSavedAt(Date.now());
    } catch {
      // ProposalBuilder surfaces the failure and rolls the value back; the job here
      // is to not flash "Saved" for a change the server rejected.
      setPriceTierSavedAt(null);
    } finally {
      setIsSavingPriceTier(false);
    }
  };

  const applyVatExempt = async () => {
    const nextData = {
      ...data,
      vat_exempt: draftVatExempt,
    };
    if (!onApplyAdjustment) {
      onChange(nextData);
      return;
    }
    setIsSavingVatExempt(true);
    try {
      await onApplyAdjustment(nextData);
      setVatExemptSavedAt(Date.now());
    } catch {
      // ProposalBuilder surfaces the failure and rolls the value back; the job here
      // is to not flash "Saved" for a change the server rejected.
      setVatExemptSavedAt(null);
    } finally {
      setIsSavingVatExempt(false);
    }
  };

  const draftPercentageValue = parsePercentageInput(draftPercentageInput);

  const hasPendingAdjustmentChange =
    draftAdjustmentType !== adjustmentType ||
    Math.abs(draftPercentageValue - adjustmentPercentage) > 1e-9;

  // A commission or discount needs a percentage: with the field empty (or zeroed) there is
  // nothing to commit, and applying would quietly store "no adjustment" instead. "none" is
  // deliberately exempt, since selecting it is how an existing adjustment gets removed.
  const isAdjustmentDraftIncomplete =
    draftAdjustmentType !== "none" && draftPercentageValue === 0;

  const canApplyAdjustment =
    !readOnly && !isSavingAdjustment && hasPendingAdjustmentChange && !isAdjustmentDraftIncomplete;

  const financialSummary = computeProposalFinancialSummary({
    pax_adults,
    pax_children,
    pax_infants,
    clients: data.clients || {},
    proposal_tours: tours,
    proposal_attractions: attractions,
    proposal_transfers: transfers,
    proposal_flights: flights,
    proposal_airport_services: airportServices,
    proposal_accommodations: accommodations,
    proposal_additional_requests: additionalRequests,
    proposal_insurance: insurance,
    proposal_handling_fee: handlingFee,
    pricing_adjustment_type: data?.pricing_adjustment_type,
    pricing_adjustment_percentage: data?.pricing_adjustment_percentage,
    price_tier_override: data?.price_tier_override,
    vat_exempt: data?.vat_exempt,
  });

  // Preview of the draft adjustment for the inline indicator. The committed
  // total below stays bound to the committed financialSummary.
  const draftAdjustmentAmountPreview = (() => {
    if (draftAdjustmentType === "none" || draftPercentageValue === 0) return 0;
    const base = financialSummary.totalPayableBeforeAdjustment;
    const signed = (base * draftPercentageValue) / 100;
    return draftAdjustmentType === "discount" ? -signed : signed;
  })();

  // Only a discount is capped; a commission takes whatever markup the deal needs.
  const percentageCeilingFor = (type: string) =>
    type === "discount" ? MAX_DISCOUNT_PERCENTAGE : undefined;

  // Every type change starts from an empty field. A markup and a discount are not the same
  // number, and there is no percentage worth guessing on the operator's behalf — Apply stays
  // disabled until one is typed.
  const handleDraftTypeChange = (value: string) => {
    setDraftAdjustmentType(value);
    setDraftPercentageInput("");
  };

  const handleDraftPercentageChange = (raw: string) => {
    const masked = maskPercentageInput(raw, percentageCeilingFor(draftAdjustmentType));
    setDraftPercentageInput(masked);
    if (parsePercentageInput(masked) > 0 && draftAdjustmentType === "none") {
      setDraftAdjustmentType("commission");
    }
  };

  // Settles the field once the operator leaves it: "12," becomes "12", "0" becomes empty.
  const handleDraftPercentageBlur = () => {
    setDraftPercentageInput(formatPercentageForDisplay(draftPercentageValue));
  };

  const percentageDisabled = readOnly || draftAdjustmentType === "none";

  const applyAdjustment = async () => {
    const effectiveType = draftPercentageValue === 0 ? "none" : draftAdjustmentType;
    const effectivePercentage = effectiveType === "none" ? 0 : draftPercentageValue;
    const nextData = {
      ...data,
      pricing_adjustment_type: effectiveType,
      pricing_adjustment_percentage: effectivePercentage,
    };
    if (!onApplyAdjustment) {
      onChange(nextData);
      return;
    }
    setIsSavingAdjustment(true);
    try {
      await onApplyAdjustment(nextData);
      setAdjustmentSavedAt(Date.now());
    } catch {
      // ProposalBuilder surfaces the failure and rolls the value back; the job here
      // is to not flash "Saved" for a change the server rejected.
      setAdjustmentSavedAt(null);
    } finally {
      setIsSavingAdjustment(false);
    }
  };

  // computeProposalFinancialSummary returns per-category values WITH the
  // adjustment already applied. To render the strike-through pre-adjustment
  // amount we divide by the same factor. Categories where `adjustable=false`
  // (handling fee) skip the strike-through entirely.
  const hasAppliedAdjustment = adjustmentType !== "none" && adjustmentPercentage > 0;
  const adjustmentFactor =
    adjustmentType === "commission" ? 1 + adjustmentPercentage / 100 :
    adjustmentType === "discount"   ? 1 - adjustmentPercentage / 100 :
    1;

  const renderCategoryAmount = (adjustedAmount: number, adjustable: boolean = true) => {
    if (!adjustable || adjustedAmount === 0 || !hasAppliedAdjustment || adjustmentFactor === 0) {
      return <span className="text-sm font-semibold text-primary tabular-nums">{formatSummaryMoney(adjustedAmount)}</span>;
    }
    const preAdjusted = adjustedAmount / adjustmentFactor;
    return (
      <span className="inline-flex flex-wrap items-baseline gap-1.5">
        <span className="text-xs text-muted-foreground line-through tabular-nums">{formatSummaryMoney(preAdjusted)}</span>
        <span className="text-sm font-semibold text-primary tabular-nums">{formatSummaryMoney(adjustedAmount)}</span>
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Proposal Summary</h2>
        <p className="text-sm text-muted-foreground">Review all details before finishing the proposal</p>
      </div>

      <Card>
        <CardHeader className="px-4 py-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="h-5 w-5" />
            General Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 px-4 pb-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Title</p>
              <p className="font-medium">{title || "Not specified"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Destination</p>
              <p className="font-medium">{main_destination || "Not specified"}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Passengers</p>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <p className="font-medium">
                  {totalPax} total ({pax_adults || 0} adults, {pax_children || 0} children, {pax_infants || 0} infants)
                </p>
              </div>
            </div>
            {passenger_mode_detailed && passengers.length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground">Passenger Details</p>
                <p className="font-medium">{passengers.length} passengers registered</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Plane className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-xl font-semibold">{flights.length}</p>
                  <p className="text-xs text-muted-foreground">Flights</p>
                </div>
              </div>
              <div>
                {flights.length === 0
                  ? <span className="text-sm font-semibold text-primary">No service</span>
                  : renderCategoryAmount(financialSummary.flights.base)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Car className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-xl font-semibold">{transfers.length}</p>
                  <p className="text-xs text-muted-foreground">Transfers</p>
                </div>
              </div>
              <div>{renderCategoryAmount(financialSummary.transfers.base)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <MapPin className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-xl font-semibold">{tours.length}</p>
                  <p className="text-xs text-muted-foreground">Tours</p>
                </div>
              </div>
              <div>{renderCategoryAmount(financialSummary.tours.base)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Ticket className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-xl font-semibold">{attractions.length}</p>
                  <p className="text-xs text-muted-foreground">Attractions</p>
                </div>
              </div>
              <div>{renderCategoryAmount(financialSummary.attractions.base)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Plane className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-xl font-semibold">{airportServices.length}</p>
                  <p className="text-xs text-muted-foreground">Airport Services</p>
                </div>
              </div>
              <div>{renderCategoryAmount(financialSummary.airport.base)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Hotel className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-xl font-semibold">{accommodations.length}</p>
                  <p className="text-xs text-muted-foreground">Accommodations</p>
                </div>
              </div>
              <div>
                {accommodations.length === 0
                  ? <span className="text-sm font-semibold text-primary">No service</span>
                  : accommodations.every((acc: any) => acc.to_be_quoted)
                    ? <span className="text-sm font-semibold text-primary">To Be Quoted</span>
                    : renderCategoryAmount(financialSummary.accommodations.base)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Plus className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-xl font-semibold">{additionalRequests.length}</p>
                  <p className="text-xs text-muted-foreground">Additional Requests</p>
                </div>
              </div>
              <div>{renderCategoryAmount(financialSummary.additionalRequests.base)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-xl font-semibold">{insurance.length}</p>
                  <p className="text-xs text-muted-foreground">Insurance</p>
                </div>
              </div>
              <div>{renderCategoryAmount(financialSummary.insurance.base)}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3 px-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <DollarSign className="h-6 w-6 text-primary" />
                <div>
                  <p className="text-xl font-semibold">{handlingFee.length}</p>
                  <p className="text-xs text-muted-foreground">Handling Fee</p>
                </div>
              </div>
              <div>{renderCategoryAmount(handlingFee.length === 0 ? 0 : financialSummary.handlingFee.base, false)}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mx-auto grid w-full max-w-6xl gap-6 lg:grid-cols-2 lg:items-stretch">
      <Card className="flex h-full w-full flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b bg-primary/5 px-4 py-3 sm:px-5">
          <div className="flex items-center gap-2">
            <ReceiptText className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold">Total Proposal Value</h3>
          </div>
          <span className="rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
            Adjustment is internal · not shown to client
          </span>
        </div>
        <CardContent className="flex flex-1 flex-col space-y-1 px-4 py-4 sm:px-5">
          <div className="flex items-baseline justify-between py-1 text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-semibold tabular-nums">{formatSummaryMoney(financialSummary.totalBase)}</span>
          </div>
          <div className="flex items-baseline justify-between py-1 text-sm">
            <span className="text-muted-foreground">
              Taxes
              {data?.vat_exempt && (
                <span className="ml-2 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200">
                  VAT Exempt
                </span>
              )}
            </span>
            <span className="font-semibold tabular-nums">{formatSummaryMoney(financialSummary.totalTaxes)}</span>
          </div>

          <div className="my-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-500/40 dark:bg-amber-500/10">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-900 dark:text-amber-200">
                VAT exempt
              </span>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="summary_vat_exempt"
                  checked={draftVatExempt}
                  onCheckedChange={(checked) => setDraftVatExempt(Boolean(checked))}
                  disabled={readOnly}
                />
                <label htmlFor="summary_vat_exempt" className="cursor-pointer text-sm text-amber-900 dark:text-amber-200">
                  Destination outside the UAE
                </label>
              </div>
              <Button
                type="button"
                onClick={applyVatExempt}
                disabled={readOnly || isSavingVatExempt || !hasPendingVatExemptChange}
                className="h-9 bg-amber-700 px-3 text-sm font-semibold text-white hover:bg-amber-800 disabled:opacity-60"
              >
                {isSavingVatExempt ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Apply"
                )}
              </Button>
              {!hasPendingVatExemptChange && vatExemptSavedAt && !isSavingVatExempt && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="h-3 w-3" />
                  Saved
                </span>
              )}
            </div>
          </div>

          {/* Three sections on this card each own an "Apply" button; the test id scopes
              assertions to this one instead of relying on document order. */}
          <div
            data-testid="internal-adjustment"
            className="my-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-500/40 dark:bg-amber-500/10"
          >
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-900 dark:text-amber-200">
                Internal adjustment
              </span>
              <Select value={draftAdjustmentType} onValueChange={handleDraftTypeChange} disabled={readOnly}>
                <SelectTrigger className="h-9 w-[180px] border-amber-300 bg-background text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No adjustment</SelectItem>
                  <SelectItem value="commission">Commission / Add-on</SelectItem>
                  <SelectItem value="discount">Discount</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Input
                  type="text"
                  inputMode="decimal"
                  value={draftPercentageInput}
                  placeholder="0"
                  onChange={(e) => handleDraftPercentageChange(e.target.value)}
                  onBlur={handleDraftPercentageBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && canApplyAdjustment) {
                      e.preventDefault();
                      applyAdjustment();
                    }
                  }}
                  disabled={percentageDisabled}
                  className="h-9 w-28 border-amber-300 bg-background pr-9 text-right text-sm font-semibold tabular-nums"
                  aria-label="Adjustment percentage"
                  aria-describedby="adjustment-percentage-hint"
                  title={
                    draftAdjustmentType === "discount"
                      ? `Free-form percentage up to ${MAX_DISCOUNT_PERCENTAGE}%, with up to two decimals`
                      : "Free-form percentage, with up to two decimals"
                  }
                />
                {/* Right-aligned text keeps the number tight against this suffix, so the
                    field always reads as one unit ("12,5 %") however long the number is. */}
                <span
                  aria-hidden="true"
                  className={cn(
                    "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold",
                    percentageDisabled ? "text-muted-foreground/50" : "text-amber-900 dark:text-amber-200"
                  )}
                >
                  %
                </span>
              </div>
              <Button
                type="button"
                onClick={applyAdjustment}
                disabled={!canApplyAdjustment}
                className="h-9 bg-amber-700 px-3 text-sm font-semibold text-white hover:bg-amber-800 disabled:opacity-60"
              >
                {isSavingAdjustment ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Apply"
                )}
              </Button>
              {!hasPendingAdjustmentChange && adjustmentSavedAt && !isSavingAdjustment && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="h-3 w-3" />
                  Saved
                </span>
              )}
              <span className="ml-auto text-sm font-semibold text-amber-900 tabular-nums dark:text-amber-200">
                {draftAdjustmentType === "discount" ? "− " : "+ "}
                {formatSummaryMoney(Math.abs(draftAdjustmentAmountPreview))}
              </span>
            </div>
            <p
              id="adjustment-percentage-hint"
              className="mt-1.5 text-[11px] font-medium text-amber-900 dark:text-amber-200"
            >
              {isAdjustmentDraftIncomplete
                ? <>Type a percentage to enable <strong>Apply</strong>.</>
                : hasPendingAdjustmentChange
                ? <>Press <strong>Apply</strong> to commit the new total below.</>
                : draftAdjustmentType === "discount"
                  ? `Type any percentage up to ${MAX_DISCOUNT_PERCENTAGE}%, with up to two decimals.`
                  : "Type any percentage, with up to two decimals."}
            </p>
          </div>

          <div className="my-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-500/40 dark:bg-amber-500/10">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-900 dark:text-amber-200">
                Price tier
              </span>
              <Select value={draftPriceTier} onValueChange={setDraftPriceTier} disabled={readOnly}>
                <SelectTrigger className="h-9 w-[200px] border-amber-300 bg-background text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (client default)</SelectItem>
                  <SelectItem value="agency">Agency</SelectItem>
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="final">Final client</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="button"
                onClick={applyPriceTier}
                disabled={readOnly || isSavingPriceTier || !hasPendingPriceTierChange}
                className="h-9 bg-amber-700 px-3 text-sm font-semibold text-white hover:bg-amber-800 disabled:opacity-60"
              >
                {isSavingPriceTier ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Apply"
                )}
              </Button>
              {!hasPendingPriceTierChange && priceTierSavedAt && !isSavingPriceTier && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="h-3 w-3" />
                  Saved
                </span>
              )}
            </div>
            {draftPriceTier !== "auto" && (
              <p className="mt-1.5 text-[11px] font-medium text-amber-900 dark:text-amber-200">
                Forcing the <strong>{draftPriceTier === "final" ? "final client" : draftPriceTier}</strong> price tier for every line item (hotels keep their meal-plan pricing).
              </p>
            )}
          </div>

          <div className="mt-auto flex items-baseline justify-between border-t pt-3">
            <span className="text-base font-semibold">Total</span>
            <span className="text-2xl font-bold text-primary tabular-nums">
              {formatSummaryMoney(financialSummary.totalPayable)}
            </span>
          </div>
        </CardContent>
      </Card>

      {(onPreview || onGeneratePdf || onFinish) && (() => {
        const currentLanguage = data?.language === "pt_br" ? "pt_br" : "en";
        const currentCurrency = proposalCurrency.toLowerCase() === "aed" ? "aed" : "usd";
        const adjustedTotal = financialSummary.totalPayable;
        const baseTotal = financialSummary.totalPayableBeforeAdjustment;
        const hasAdjustment = adjustmentType !== "none" && adjustmentPercentage > 0;
        const busy = Boolean(loading || previewingProposal || generatingPdf);
        const showViewCta = readOnly || isNeedsFixSales;

        return (
          <Card className="flex h-full flex-col border-2 border-primary/40 bg-gradient-to-br from-blue-50/60 via-background to-emerald-50/60 dark:from-blue-500/5 dark:to-emerald-500/5">
            <CardHeader className="px-4 py-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Send className="h-5 w-5 text-primary" />
                Ready to send the proposal?
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col space-y-4 px-4 pb-4">
              <div className="grid gap-4 rounded-lg border bg-background p-3 md:grid-cols-2">
                <div>
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Language</p>
                  <div className="flex flex-wrap gap-1.5">
                    <ToggleChip
                      active={currentLanguage === "pt_br"}
                      onClick={() => onChange({ ...data, language: "pt_br" })}
                      disabled={readOnly}
                      activeTone="blue"
                      icon={<BrazilFlag className="w-4 h-3 rounded-sm" />}
                      label="Português"
                    />
                    <ToggleChip
                      active={currentLanguage === "en"}
                      onClick={() => onChange({ ...data, language: "en" })}
                      disabled={readOnly}
                      activeTone="blue"
                      icon={<USAFlag className="w-4 h-3 rounded-sm" />}
                      label="English"
                    />
                  </div>
                </div>
                <div>
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Currency</p>
                  <div className="flex flex-wrap gap-1.5">
                    <ToggleChip
                      active={currentCurrency === "usd"}
                      onClick={() => onChange({ ...data, currency: "usd" })}
                      disabled={readOnly}
                      activeTone="emerald"
                      icon={<CircleDollarSign className="h-4 w-4" />}
                      label="USD"
                    />
                    <ToggleChip
                      active={currentCurrency === "aed"}
                      onClick={() => onChange({ ...data, currency: "aed" })}
                      disabled={readOnly}
                      activeTone="emerald"
                      icon={<Landmark className="h-4 w-4" />}
                      label="AED"
                    />
                  </div>
                </div>
              </div>

              <div className={`grid gap-3 ${hasAdjustment ? "md:grid-cols-2" : "grid-cols-1"}`}>
                <div className={`rounded-lg p-3 ${hasAdjustment ? "border-2 border-emerald-300 bg-emerald-50/40 dark:border-emerald-500/40 dark:bg-emerald-500/5" : "border bg-background"}`}>
                  <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      <p className="text-sm font-semibold">Final values</p>
                    </div>
                    <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200">
                      For client
                    </span>
                  </div>
                  <p className="mb-3 text-[11px] text-muted-foreground">
                    {hasAdjustment ? `With ${adjustmentPercentage}% ${adjustmentType === "discount" ? "discount" : "commission"}` : "No adjustment applied"} · Total <strong className="text-foreground">{formatSummaryMoney(adjustedTotal)}</strong>
                  </p>
                  {/* One button per row: once both value cards sit side by side, a 2-up grid
                      leaves ~70px per button and the labels spill out of the border. */}
                  <div className="grid grid-cols-1 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { void onPreview?.("adjusted"); }}
                      disabled={busy || !onPreview}
                      className="h-10 w-full min-w-0 px-3 border-emerald-300 bg-background text-emerald-800 hover:bg-emerald-50 dark:border-emerald-500/40 dark:text-emerald-200 dark:hover:bg-emerald-500/10"
                    >
                      {previewingProposal ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ExternalLink className="h-4 w-4" />
                      )}
                      <span className="truncate">Preview</span>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled
                      title="PDF generation is being reworked"
                      className="h-10 w-full min-w-0 px-3 border-emerald-300 bg-background text-emerald-800 dark:border-emerald-500/40 dark:text-emerald-200"
                    >
                      <FileText className="h-4 w-4" />
                      <span className="truncate">PDF</span>
                      <span className="shrink-0 rounded bg-muted px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Soon</span>
                    </Button>
                  </div>
                </div>

                {hasAdjustment && (
                  <div className="rounded-lg border bg-background p-3">
                    <div className="mb-1 flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm font-semibold">Net values</p>
                      </div>
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Internal
                      </span>
                    </div>
                    <p className="mb-3 text-[11px] text-muted-foreground">
                      Without adjustment · Total <strong className="text-foreground">{formatSummaryMoney(baseTotal)}</strong>
                    </p>
                    <div className="grid grid-cols-1 gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => { void onPreview?.("base"); }}
                        disabled={busy || !onPreview}
                        className="h-10 w-full min-w-0 px-3"
                      >
                        {previewingProposal ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ExternalLink className="h-4 w-4" />
                        )}
                        <span className="truncate">Preview</span>
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        disabled
                        title="PDF generation is being reworked"
                        className="h-10 w-full min-w-0 px-3"
                      >
                        <FileText className="h-4 w-4" />
                        <span className="truncate">PDF</span>
                        <span className="shrink-0 rounded bg-muted px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">Soon</span>
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-auto border-t pt-3">
                {showViewCta ? (
                  <Button
                    type="button"
                    onClick={() => { void onPreview?.("adjusted"); }}
                    disabled={busy || !onPreview}
                    className="w-full h-12 bg-blue-600 text-base font-bold text-white shadow-sm hover:bg-blue-700"
                  >
                    <ExternalLink className="mr-2 h-5 w-5" />
                    View Proposal
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={() => { void onFinish?.(); }}
                    disabled={busy || !onFinish}
                    className="w-full h-12 bg-emerald-600 text-base font-bold text-white shadow-sm hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="mr-2 h-5 w-5" />
                    Finish Proposal
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })()}
      </div>
    </div>
  );
};

export default SummaryStep;
